import {
    IAction,
    type IStep,
    useChatMessages,
    useConfig
} from '@chainlit/react-client';

import CopyButton from '@/components/CopyButton';

import DMSourceLink, { type SourceInfo } from '../DMSourceLink';
import MessageActions from './Actions';
import { DebugButton } from './DebugButton';
import { FeedbackButtons } from './FeedbackButtons';

interface Props {
    message: IStep;
    actions: IAction[];
    run?: IStep;
    contentRef?: React.RefObject<HTMLDivElement>;
    isEditingOutput?: boolean;
    hasBeenEdited?: boolean;
    onEditClick?: () => void;
    sources?: SourceInfo[];
}

const MessageButtons = ({
    message,
    actions,
    run,
    contentRef,
    isEditingOutput,
    hasBeenEdited,
    onEditClick,
    sources
}: Props) => {
    const { config } = useConfig();
    const { firstInteraction } = useChatMessages();

    const isUser = message.type === 'user_message';
    const isAsk = message.waitForAnswer;
    const hasContent = !!message.output;
    const showCopyButton = !!run && hasContent && !isUser && !isAsk;

    const messageActions = actions.filter((a) => a.forId === message.id);

    const showDebugButton =
        !!config?.debugUrl && !!message.threadId && !!firstInteraction && !!run;

    const show = showCopyButton || showDebugButton || messageActions?.length;

    if (!show || message.streaming) {
        return null;
    }

    return (
        <div className="flex flex-wrap items-center -ml-1.5">
            {showCopyButton ? (
                <>
                    <CopyButton
                        content={message.output}
                        contentRef={contentRef}
                    />
                </>
            ) : null}
            {run ? <FeedbackButtons message={run} /> : null}
            {messageActions.length ? (
                <MessageActions actions={messageActions} />
            ) : null}
            {showDebugButton ? (
                <DebugButton debugUrl={config.debugUrl!} step={message} />
            ) : null}
            {sources && sources.length > 0 ? (
                <div className="ml-2">
                    <DMSourceLink sources={sources} />
                </div>
            ) : null}
        </div>
    );
};

export { MessageButtons };
